// ============================================================================
// PATIENT CONTACTS BLOCK — Historial de contactos F5b del paciente
// Agregado de todos los presupuestos del paciente en el centro activo
// ============================================================================
import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Phone, MessageSquare, AlertTriangle } from 'lucide-react';
import { budgetContactsApi, type BudgetContactDto } from '../../api/budget-contacts';
import { CollapsibleSection } from '../ui/CollapsibleSection';

interface PatientContactsBlockProps {
  patientId: string;
  userId?: string;
}

const INITIAL_LIMIT = 10;

function formatDateTime(iso: string | null): string {
  if (!iso) return '—';
  try {
    const d = new Date(iso);
    return d.toLocaleString('es-ES', {
      day: '2-digit', month: '2-digit', year: 'numeric',
      hour: '2-digit', minute: '2-digit',
    });
  } catch {
    return iso;
  }
}

export function PatientContactsBlock({ patientId, userId }: PatientContactsBlockProps) {
  const navigate = useNavigate();
  const [contacts, setContacts] = useState<BudgetContactDto[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [hasLoaded, setHasLoaded] = useState(false);
  const [limit, setLimit] = useState(INITIAL_LIMIT);

  const load = useCallback(async () => {
    if (hasLoaded) return;
    setIsLoading(true);
    setError(null);
    try {
      const result = await budgetContactsApi.getByPatient(patientId);
      setContacts(result);
      setHasLoaded(true);
    } catch (err: any) {
      setError(err?.message || 'Error al cargar el historial de contactos');
    } finally {
      setIsLoading(false);
    }
  }, [patientId, hasLoaded]);

  useEffect(() => { load(); }, [load]);

  const visible = contacts.slice(0, limit);
  const hasMore = contacts.length > limit;

  return (
    <CollapsibleSection
      id="contacts"
      title="Historial de contactos"
      icon={<MessageSquare className="h-5 w-5 text-gray-400" />}
      badge={
        <span className="text-sm font-normal text-gray-500">
          {contacts.length} {contacts.length === 1 ? 'contacto' : 'contactos'}
        </span>
      }
      defaultOpen={false}
      userId={userId}
    >
      {error && (
        <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-2.5 rounded-lg text-sm mb-3">
          {error}
        </div>
      )}
      {isLoading && !hasLoaded ? (
        <p className="text-sm text-gray-400">Cargando...</p>
      ) : contacts.length === 0 ? (
        <p className="text-sm text-gray-500 italic py-4 text-center">
          No hay contactos registrados para este paciente.
        </p>
      ) : (
        <div className="space-y-2">
          {visible.map((c) => (
            <button
              key={c.id}
              type="button"
              onClick={() => navigate(`/presupuestos/${c.budgetId}`)}
              className="w-full text-left p-3 rounded-lg border border-gray-200 hover:border-gray-300 hover:bg-gray-50 transition-colors"
            >
              <div className="flex items-start justify-between gap-3">
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <Phone className="h-4 w-4 text-gray-400 shrink-0" />
                    <span className="text-sm font-medium text-gray-900">{c.contactTypeName}</span>
                    {c.triggersRejection && (
                      <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded bg-red-50 text-red-700 text-xs font-medium border border-red-200">
                        <AlertTriangle className="h-3 w-3" /> Provocó rechazo
                      </span>
                    )}
                    {c.isNoAnswer && (
                      <span className="px-1.5 py-0.5 rounded bg-amber-50 text-amber-700 text-xs font-medium border border-amber-200">
                        Sin respuesta
                      </span>
                    )}
                  </div>
                  <p className="text-xs text-gray-500 mt-0.5">
                    {formatDateTime(c.createdAt)}
                    {' · '}
                    <span className="font-mono">{c.budgetNumber}</span>
                    {c.createdByName && <span> · {c.createdByName}</span>}
                  </p>
                  {c.notes && (
                    <p className="text-sm text-gray-700 mt-1.5 whitespace-pre-wrap line-clamp-3">{c.notes}</p>
                  )}
                  {c.scheduledAt && (
                    <p className="text-xs text-blue-700 mt-1">
                      Próxima llamada: {formatDateTime(c.scheduledAt)}
                    </p>
                  )}
                </div>
              </div>
            </button>
          ))}
          {hasMore && (
            <button
              type="button"
              onClick={() => setLimit((prev) => prev + INITIAL_LIMIT)}
              className="w-full py-2 text-sm text-[#008DD0] hover:text-[#007ab8] font-medium"
            >
              Ver {Math.min(INITIAL_LIMIT, contacts.length - limit)} más
            </button>
          )}
        </div>
      )}
    </CollapsibleSection>
  );
}

export default PatientContactsBlock;
